"use client";

import React from "react";
import { Phone, Mail, MapPin, MessageCircle, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const contactInfo = [{
  icon: Phone,
  title: "ENQUIRIES",
  value: "+1 (555) 888-2024",
  link: "tel:+15558882024",
  description: "Speak with our lead planner."
}, {
  icon: MessageCircle,
  title: "WHATSAPP",
  value: "+1 (555) 888-2025",
  link: "https://wa.me/15558882025",
  description: "Instant luxury consultations."
}, {
  icon: Mail,
  title: "EMAIL",
  value: "hello@signatureoccasions.com",
  link: "mailto:hello@signatureoccasions.com",
  description: "For detailed event proposals."
}, {
  icon: MapPin,
  title: "STUDIO",
  value: "72nd Avenue, Manhattan, NY",
  link: "https://maps.google.com/?q=Manhattan+New+York",
  description: "By appointment only."
}];
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 bg-accent/30 border border-primary/10 rounded-lg text-center space-y-4">
        <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center mb-2">
          <Mail className="size-6 text-primary" />
        </div>
        <h3 className="text-xl font-default text-foreground">Inquiry Received</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-md">
          Send another inquiry
        </Button>
      </div>;
  }
  const obsidianInputClass = "bg-background/50 border-x-0 border-t-0 border-b border-secondary focus-visible:border-primary focus-visible:ring-0 rounded-md px-2 text-foreground placeholder:text-muted-foreground/40 transition-colors font-default";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-default uppercase tracking-widest text-xs">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Evelyn Sterling" className={obsidianInputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-default uppercase tracking-widest text-xs">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="evelyn@example.com" className={obsidianInputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground font-default uppercase tracking-widest text-xs">Phone Number (Optional)</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" className={obsidianInputClass} {...field} />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground/60">
              For a more personal consultation experience.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground font-default uppercase tracking-widest text-xs">Event Vision</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your dream occasion..." className={`min-h-[120px] resize-none ${obsidianInputClass}`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="subscribe" render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 bg-accent/20 border border-border/30 rounded-md">
            <FormControl>
              <input type="checkbox" className="mt-1 size-4 shrink-0 rounded-md border-primary bg-transparent text-primary focus:ring-primary focus:ring-offset-background" checked={field.value} onChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-foreground cursor-pointer font-default text-sm">
                Join the Signature Circle
              </FormLabel>
              <FormDescription className="text-xs text-muted-foreground">
                Receive exclusive event inspiration and seasonal planning guides.
              </FormDescription>
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-default font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-500 shadow-lg hover:shadow-xl rounded-md tracking-widest uppercase">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Processing...
          </> : "Book a Consultation"}
      </Button>
    </Form>;
}
export default function StartYourJourney() {
  return <section data-section-id="241920" id="start-your-journey" className="bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-accent/10 pointer-events-none" />
      
      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          
          {}
          <div className="lg:col-span-5 space-y-12">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-6xl font-default font-light text-foreground tracking-tight">
                Start Your Journey
              </h2>
              <p className="text-xl font-default text-muted-foreground max-w-md leading-relaxed">
                Let us curate an atmosphere that speaks to your soul. Reach out to begin planning your signature occasion.
              </p>
            </div>

            <div className="space-y-8">
              {contactInfo.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-6 group">
                  <div className="flex-shrink-0 size-14 rounded-md bg-secondary/30 border border-secondary flex items-center justify-center group-hover:bg-accent group-hover:border-primary/30 transition-colors duration-300 shadow-sm">
                    <item.icon className="size-6 text-primary" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-xs font-default font-semibold text-muted-foreground uppercase tracking-widest">
                      {item.title}
                    </h3>
                    <Link to={item.link} className="block text-xl font-default text-foreground hover:text-primary transition-colors">
                      {item.value}
                    </Link>
                    <p className="text-sm font-default text-muted-foreground/70">
                      {item.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          {}
          <div className="lg:col-span-7 bg-card/40 border border-border/40 p-8 md:p-12 rounded-lg backdrop-blur-md shadow-2xl">
            <div className="mb-8">
              <h3 className="text-3xl font-default font-light text-foreground mb-2">The Consultation</h3>
              <p className="text-muted-foreground font-default">Share your details below, and our concierge will reach out within 24 hours.</p>
            </div>
            
            <FormProvider formKey="journey_form_lets_talk" formId="journey_form_lets_talk" sectionName="Start Your Journey" defaultValues={{
            name: "",
            email: "",
            phone: "",
            message: "",
            subscribe: false
          }} submitText="Book a Consultation" successMessage="Thank you for reaching out. We look forward to crafting something extraordinary together." errorMessage="There was an error processing your request. Please try again or call us directly.">
              <ContactForm />
            </FormProvider>
          </div>

        </div>
      </div>
    </section>;
}